import React, { useState, useEffect, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import PoseOverlay from '../capture/PoseOverlay';
import { useFacePose } from '../capture/useFacePose';
import { useAutoCapture } from '../capture/useAutoCapture';
import { SELFIE_PROMPTS } from '../capture/overlayConfig';

export default function LiveCaptureModal({
  isOpen,
  onClose,
  onCapture,
  stage = 'Present_Neutral', // 'Present_Neutral' (present), 'Pose_Variation_A' (post_a), 'Pose_Variation_B' (post_b)
  poseNumber = 1,
  currentCount = 0,
  maxCount = 6,
  title = "Live Camera Capture",
  description = "Align your face in the oval to capture"
}) {
  const webcamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('user');
  const [capturedFlash, setCapturedFlash] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { poseState, detectInVideo } = useFacePose();

  // Run face tracking loop when camera modal is active
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    const interval = setInterval(() => {
      if (!isMounted) return;
      if (webcamRef.current?.video && webcamRef.current.video.readyState === 4) {
        detectInVideo(webcamRef.current.video, stage, poseNumber);
      }
    }, 140); // Fast 7 fps evaluation

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isOpen, stage, poseNumber, detectInVideo]);

  const handleCaptureResult = useCallback(async (result) => {
    if (!result || !result.blob || isProcessing) return;
    setIsProcessing(true);
    setCapturedFlash(true);

    try {
      const file = new File([result.blob], `live_capture_${stage}_${poseNumber || Date.now()}.jpg`, {
        type: 'image/jpeg'
      });

      // Pass captured file and metadata to parent handler
      if (onCapture) {
        await onCapture(file, {
          width: result.width,
          height: result.height,
          capturedAt: result.capturedAt
        });
      }
    } catch (err) {
      console.error("Capture processing error:", err);
    } finally {
      setTimeout(() => {
        setCapturedFlash(false);
        setIsProcessing(false);
      }, 700);
    }
  }, [stage, poseNumber, isProcessing, onCapture]);

  const { state: overlayState, holdProgress, qualityWarning, triggerManualCapture } = useAutoCapture({
    isAligned: poseState.isAligned,
    faceDetected: poseState.faceDetected,
    faceBox: poseState.faceBox,
    videoRef: webcamRef,
    onCaptureResult: handleCaptureResult,
    enabled: isOpen && !isProcessing
  });

  // Auto-close modal when maxCount is reached
  useEffect(() => {
    if (isOpen && maxCount > 0 && currentCount >= maxCount) {
      const timer = setTimeout(() => {
        onClose();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentCount, maxCount, onClose]);

  if (!isOpen) return null;

  // Active prompt logic for Selfie variations
  const selfiePrompt = stage === 'Present_Neutral' || stage === 'present'
    ? SELFIE_PROMPTS[Math.min(currentCount, 5)]
    : null;

  const toggleCameraFacing = () => {
    setFacingMode(prev => (prev === 'user' ? 'environment' : 'user'));
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 99999,
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.75rem',
      boxSizing: 'border-box'
    }}>
      {/* Modal Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: '560px',
        backgroundColor: '#0f172a',
        borderRadius: '20px',
        border: '1px solid #334155',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Top Header Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.9rem 1.25rem',
          borderBottom: '1px solid #1e293b',
          backgroundColor: '#0f172a'
        }}>
          <div>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {stage === 'Present_Neutral' || stage === 'present' ? 'Selfie Verification' : 'Pose Verification'} • Photo {currentCount + 1} of {maxCount}
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#f8fafc', marginTop: '0.1rem' }}>
              {title}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#94a3b8',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1.25rem',
              transition: 'background 0.2s ease'
            }}
            title="Close Camera"
          >
            ✕
          </button>
        </div>

        {/* Guidance Prompt Card (especially important for Selfies variation) */}
        {selfiePrompt && (
          <div style={{
            backgroundColor: selfiePrompt.noGlasses ? 'rgba(245, 158, 11, 0.12)' : 'rgba(34, 197, 94, 0.12)',
            borderBottom: `1px solid ${selfiePrompt.noGlasses ? '#f59e0b' : '#22c55e'}`,
            padding: '0.65rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.4rem' }}>
              {selfiePrompt.noGlasses ? '👓❌' : '📸'}
            </span>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: selfiePrompt.noGlasses ? '#fef08a' : '#86efac' }}>
                {selfiePrompt.text}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#cbd5e1' }}>
                {selfiePrompt.noGlasses ? 'Please remove your glasses for this capture' : 'Change clothing, lighting or room between captures'}
              </div>
            </div>
          </div>
        )}

        {/* Responsive Camera Viewport */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: 'min(64vh, 480px)',
          backgroundColor: '#000000',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              facingMode,
              width: { ideal: 2560, min: 1280 },
              height: { ideal: 1920, min: 960 },
              aspectRatio: 4 / 3
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
            }}
          />

          {/* 6-Layer Guidance Overlay */}
          <PoseOverlay
            state={overlayState}
            pose={poseState}
            target={stage === 'present' ? 'Present_Neutral' : stage}
            poseNumber={poseNumber}
            holdProgress={holdProgress}
            qualityWarning={qualityWarning}
            activePromptText={selfiePrompt?.text}
          />

          {/* Shutter Capture Flash */}
          {capturedFlash && (
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              color: '#16a34a',
              fontWeight: 'bold',
              transition: 'opacity 0.3s ease'
            }}>
              ✓
            </div>
          )}
        </div>

        {/* Bottom Controls */}
        <div style={{
          padding: '1rem 1.25rem',
          backgroundColor: '#0f172a',
          borderTop: '1px solid #1e293b',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {/* Camera Switch button (for mobile phones) */}
            <button
              onClick={toggleCameraFacing}
              type="button"
              style={{
                padding: '0.8rem 1rem',
                backgroundColor: '#1e293b',
                color: '#f8fafc',
                border: '1px solid #334155',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                flexShrink: 0
              }}
              title="Switch Camera"
            >
              🔄 Flip
            </button>

            {/* Manual Shutter Button */}
            <button
              onClick={triggerManualCapture}
              disabled={isProcessing}
              type="button"
              style={{
                flex: 1,
                padding: '0.85rem 1.25rem',
                backgroundColor: overlayState === 'HOLDING' ? '#16a34a' : '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: overlayState === 'HOLDING' ? '0 0 15px rgba(34, 197, 94, 0.4)' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              {isProcessing ? '⚡ Saving Photo...' : (overlayState === 'HOLDING' ? '🟢 Hold Still to Snap' : '📸 Manual Snap')}
            </button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#64748b' }}>
            <span>Auto-capture triggers on 600ms green hold</span>
            <span>HD Native Quality (≥128 KB)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
